import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { useCurrentTeam } from '@/hooks/useTeams';
import { Trash2, UserPlus, Mail, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface TeamMember {
  id: string;
  user_id: string;
  role: string;
  joined_at: string;
  user_email?: string;
  user_name?: string;
}

interface TeamInvitation {
  id: string;
  invited_email: string;
  created_at: string;
  expires_at: string | null;
  accepted_at: string | null;
  role: string;
}

export const TeamMembersSection = () => {
  const { user } = useAuth();
  const { currentTeamId } = useCurrentTeam();
  const { toast } = useToast();
  
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [isLoading, setIsLoading] = useState(true);
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    if (currentTeamId) {
      loadTeamData();
    }
  }, [currentTeamId]);

  const loadTeamData = async () => {
    if (!currentTeamId) return;
    
    setIsLoading(true);
    try {
      // Load team members
      const { data: membersData, error: membersError } = await supabase
        .from('team_memberships')
        .select(`
          id,
          user_id,
          role,
          joined_at
        `)
        .eq('team_id', currentTeamId);

      if (membersError) throw membersError;

      // Get user emails and names for members
      const membersWithDetails = await Promise.all(
        (membersData || []).map(async (member) => {
          // Get user email from auth.users via a RPC call or try to get from user_profiles
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('first_name, last_name')
            .eq('user_id', member.user_id)
            .maybeSingle();

          // Try to get user auth data - we'll need to query via RPC or use a different approach
          // For now, we'll get what we can from profiles and use a placeholder for email
          let userEmail = 'Unknown email';
          
          // Try to get the user's auth email if this is the current user
          if (member.user_id === user?.id) {
            userEmail = user.email || 'Unknown email';
          } else {
            // For other users, we'll need to implement a way to get their email
            // This could be done via an edge function or by storing emails in user_profiles
            userEmail = 'Contact admin for email';
          }

          return {
            ...member,
            user_name: profileData ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() : 'Unknown User',
            user_email: userEmail
          };
        })
      );

      setMembers(membersWithDetails);

      // Load pending invitations
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('team_id', currentTeamId)
        .is('accepted_at', null)
        .gt('expires_at', new Date().toISOString()); // Only non-expired invitations

      if (invitationsError) throw invitationsError;
      setInvitations(invitationsData || []);

    } catch (error) {
      console.error('Error loading team data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const inviteMember = async () => {
    if (!newMemberEmail.trim() || !currentTeamId || !user) return;

    const emailToInvite = newMemberEmail.trim().toLowerCase();
    
    setIsInviting(true);
    try {
      // Check if email is already a team member (by comparing emails we have)
      const existingMemberEmail = members.find(member => 
        member.user_email?.toLowerCase() === emailToInvite ||
        (member.user_id === user.id && user.email?.toLowerCase() === emailToInvite)
      );

      if (existingMemberEmail) {
        toast({
          title: 'User already a member',
          description: 'This email is already part of the team',
          variant: 'destructive'
        });
        setIsInviting(false);
        return;
      }

      // Check if there's already a pending invitation
      const existingInvite = invitations.find(invite => 
        invite.invited_email.toLowerCase() === emailToInvite
      );

      if (existingInvite) {
        toast({
          title: 'Invitation already sent',
          description: 'An invitation has already been sent to this email',
          variant: 'destructive'
        });
        setIsInviting(false);
        return;
      }

      // Create invitation
      const { error: inviteError } = await supabase
        .from('team_invitations')
        .insert({
          team_id: currentTeamId,
          invited_email: emailToInvite,
          invited_by: user.id,
          role: selectedRole,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
        });

      if (inviteError) throw inviteError;

      toast({
        title: 'Invitation sent!',
        description: `Invitation sent to ${emailToInvite}`,
      });

      setNewMemberEmail('');
      loadTeamData(); // Refresh the data

    } catch (error) {
      console.error('Error inviting member:', error);
      toast({
        title: 'Error',
        description: 'Failed to send invitation',
        variant: 'destructive'
      });
    } finally {
      setIsInviting(false);
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('team_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;

      toast({
        title: 'Invitation cancelled',
        description: 'The invitation has been cancelled',
      });

      loadTeamData(); // Refresh the data
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel invitation',
        variant: 'destructive'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Invite New Member */}
      <div className="border rounded-lg p-4">
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          Invite Team Member
        </h3>
        <div className="space-y-3">
          <div className="flex-1">
            <Label htmlFor="memberEmail" className="text-sm font-medium">Email Address</Label>
            <Input
              id="memberEmail"
              type="email"
              placeholder="Enter email address"
              value={newMemberEmail}
              onChange={(e) => setNewMemberEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && inviteMember()}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="memberRole" className="text-sm font-medium">Role</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner - Full access and team management</SelectItem>
                <SelectItem value="editor">Editor - Can edit businesses and contact info</SelectItem>
                <SelectItem value="analyst">Analyst - Can view and analyze businesses</SelectItem>
                <SelectItem value="viewer">Viewer - Read-only access</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={inviteMember} 
            disabled={!newMemberEmail.trim() || isInviting}
            className="w-full"
          >
            {isInviting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Send Invite
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Current Members */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Team Members ({members.length})</h3>
        {members.length === 0 ? (
          <p className="text-muted-foreground">No team members yet.</p>
        ) : (
          <div className="space-y-2">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium">
                      {member.user_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{member.user_name || 'Unknown User'}</p>
                    <p className="text-sm text-muted-foreground">{member.user_email}</p>
                    <p className="text-xs text-muted-foreground">
                      Joined {new Date(member.joined_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
                    {member.role}
                  </Badge>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                  {member.role !== 'owner' && (
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Pending Invitations ({invitations.length})</h3>
          <div className="space-y-2">
            {invitations.map((invitation) => {
              const isExpired = invitation.expires_at && new Date(invitation.expires_at) < new Date();
              
              return (
                <div key={invitation.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{invitation.invited_email}</p>
                      <p className="text-sm text-muted-foreground">
                        Invited {new Date(invitation.created_at).toLocaleDateString()}
                        {invitation.expires_at && (
                          <> • {isExpired ? 'Expired' : 'Expires'} {new Date(invitation.expires_at).toLocaleDateString()}</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={isExpired ? "destructive" : "outline"} className="bg-orange-50 text-orange-700 border-orange-200">
                      <Clock className="h-3 w-3 mr-1" />
                      {isExpired ? 'Expired' : 'Pending'}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-destructive hover:text-destructive"
                      onClick={() => cancelInvitation(invitation.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};